import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';
import { HotelIcon, HomeIcon, AuctionIcon, MapPinIcon, EventIcon, ExperienceIcon } from '../utils/IconWrapper';
import Section from '../components/ui/Section';
import Button from '../components/ui/Button';
import { Helmet } from 'react-helmet';
import useModal from '../utils/useModal';

// Immagini dei servizi
const shortTermRentalImage = 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1350&q=80';
const longTermRentalImage = 'https://images.unsplash.com/photo-1484154218962-a197022b5858?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1350&q=80';
const auctionsImage = 'https://geauction.com/wp-content/uploads/2018/06/Your-First-Auction-What-You-Need-to-Know.jpg';
const buyAndSellImage = 'https://images.unsplash.com/photo-1560448204-603b3fc33ddc?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1350&q=80';
const eventsImage = 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80';
const experiencesImage = 'https://www.dailymood.it/wp-content/uploads/2018/04/home-35.jpg';

// Modal type enum
type ModalType = 'shortTerm' | 'longTerm' | null;

const PageContainer = styled.div`
  background-color: #000;
  color: #fff;
  min-height: 100vh;
  padding-top: 70px;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 1rem;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const SectionTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1.5rem;
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 2px;
    background-color: var(--accent-primary);
  }
`;

const SectionDescription = styled.p`
  max-width: 800px;
  margin: 0 auto;
  color: #888;
  font-size: 1.2rem;
  line-height: 1.6;
`;

const ServiceSection = styled.section`
  display: flex;
  position: relative;
  margin: 4rem 0;
  
  @media (max-width: 992px) {
    flex-direction: column;
  }
`;

const Column = styled.div<{ $isRight?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 2rem;
  order: ${props => props.$isRight ? 2 : 1};
  
  @media (max-width: 992px) {
    order: ${props => props.$isRight ? 2 : 1};
    padding: 1.5rem 0;
  }
`;

const ServiceImage = styled.div<{ $backgroundImage: string }>`
  position: relative;
  width: 100%;
  height: 300px;
  background-image: url(${props => props.$backgroundImage});
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  overflow: hidden;
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const ServiceTitle = styled.h2`
  font-size: 2.2rem;
  margin-top: 0;
  margin-bottom: 1.5rem;
  position: relative;
  padding-bottom: 1rem;
  color: #004744;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 2px;
    background-color: #004744;
  }
`;

const ServiceSubtitle = styled.div`
  font-size: 1.2rem;
  color: #888;
  margin-bottom: 1.5rem;
  position: relative;
  padding-bottom: 1rem;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 2px;
    background-color: #333;
  }
`;

const ServiceDescription = styled.p`
  line-height: 1.6;
  margin-bottom: 1.5rem;
  max-width: 600px;
  font-size: 0.95rem;
`;

// New components for modal
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background-color: #222;
  border-radius: 8px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  color: #fff;
  border: 1px solid #444;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.3);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: transparent;
  border: 1px solid #666;
  border-radius: 50%;
  width: 2.5rem;
  height: 2.5rem;
  font-size: 1.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 110;
  color: #fff;
  transition: background-color 0.2s, transform 0.2s;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    transform: scale(1.1);
  }
`;

const ModalBody = styled.div`
  padding: 2.5rem;
`;

const ModalTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: #fff;
  text-align: center;
  padding-bottom: 1rem;
  border-bottom: 1px solid #444;
`;

const ModalDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 2rem;
  color: #ddd;
  text-align: center;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ActionButton = styled(Button)`
  flex: 1;
  text-align: center;
  padding: 1rem;
  font-size: 1.1rem;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const Services: React.FC = () => {
  const { language } = useLanguage();
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  
  // Use our new useModal hook
  const { isOpen: isModalOpen, openModal: openModalHandler, closeModal: closeModalHandler } = useModal();
  
  const openModal = (type: ModalType) => {
    setActiveModal(type);
    openModalHandler();
  };
  
  const closeModal = () => {
    setActiveModal(null);
    closeModalHandler();
  };
  
  // Clean up on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);
  
  return (
    <PageContainer>
      <Helmet>
        <title>{language === 'it' ? 'I Nostri Servizi | J-O-H' : 'Our Services | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Scopri la nostra gamma completa di servizi premium, dai soggiorni brevi alle aste immobiliari, tutti progettati per soddisfare le tue esigenze' 
            : 'Discover our complete range of premium services, from short stays to real estate auctions, all designed to meet your needs'} 
        />
      </Helmet>
      
      <ContentContainer>
        <SectionHeader>
          <SectionTitle>{language === 'it' ? 'Servizi in Evidenza' : 'Featured Services'}</SectionTitle>
          <SectionDescription>
            {language === 'it'
              ? "Scopri la nostra gamma completa di servizi premium, tutti progettati per soddisfare le tue esigenze immobiliari di lusso con un'attenzione impeccabile ai dettagli."
              : "Discover our complete range of premium services, all designed to meet your luxury real estate needs with impeccable attention to detail."}
          </SectionDescription>
        </SectionHeader>

        {/* Nuova Sezione: Catalogo Servizi (spostata in /services/experiences) */}
        
        {/* Servizio 1: Affitti Brevi */}
        <ServiceSection id="short-term-rentals">
          <Column>
            <TextContent>
              <ServiceTitle>{language === 'it' ? 'Affitti Brevi' : 'Short-term Rentals'}</ServiceTitle>
              <ServiceDescription>
                {language === 'it' 
                  ? 'I nostri alloggi a breve termine offrono il massimo del comfort e dello stile. Ogni proprietà è accuratamente selezionata e gestita per garantire un\'esperienza impeccabile. Dalla prenotazione al check-out, ci occupiamo di ogni dettaglio per rendere il tuo soggiorno indimenticabile.' 
                  : 'Our short-term accommodations offer the utmost in comfort and style. Each property is carefully selected and managed to ensure an impeccable experience. From booking to check-out, we take care of every detail to make your stay unforgettable.'}
              </ServiceDescription>
              <Button 
                variant="primary" 
                onClick={() => openModal('shortTerm')}
                style={{ alignSelf: 'flex-start', marginTop: '1rem', backgroundColor: '#004744', borderColor: '#004744' }}
              >
                {language === 'it' ? 'Scopri' : 'Discover'}
              </Button>
            </TextContent>
          </Column>
          <Column $isRight>
            <ServiceImage $backgroundImage={shortTermRentalImage} />
          </Column>
        </ServiceSection>
        
        {/* Servizio 2: Affitti Medio/Lungo Termine */}
        <ServiceSection id="long-term-rentals">
          <Column>
            <ServiceImage $backgroundImage={longTermRentalImage} />
          </Column>
          <Column $isRight>
            <TextContent>
              <ServiceTitle>{language === 'it' ? 'Affitti Medio/Lungo Termine' : 'Medium/Long-term Rentals'}</ServiceTitle>
              <ServiceDescription>
                {language === 'it' 
                  ? 'Per chi cerca una soluzione abitativa più stabile, offriamo alloggi a medio e lungo termine completamente arredati e dotati di ogni comfort. Ideali per trasferimenti di lavoro, ristrutturazioni della propria abitazione o per chi desidera vivere un\'esperienza prolungata in una nuova località.' 
                  : 'For those seeking a more stable housing solution, we offer fully furnished medium and long-term accommodations equipped with every comfort. Ideal for work relocations, home renovations, or for those wishing to experience an extended stay in a new location.'}
              </ServiceDescription>
              <Button 
                variant="primary" 
                to="/services/long-term-rentals"
                style={{ alignSelf: 'flex-start', marginTop: '1rem', backgroundColor: '#004744', borderColor: '#004744' }}
              >
                {language === 'it' ? 'Scopri' : 'Discover'}
              </Button>
            </TextContent>
          </Column>
        </ServiceSection>
        
        {/* Servizio 3: Aste */}
        <ServiceSection id="auctions">
          <Column>
            <TextContent>
              <ServiceTitle>{language === 'it' ? 'Aste' : 'Auctions'}</ServiceTitle>
              <ServiceDescription>
                {language === 'it' 
                  ? 'Il nostro team di esperti ti guida attraverso il complesso mondo delle aste immobiliari. Forniamo consulenza pre-asta, assistenza durante la procedura e supporto post-aggiudicazione, permettendoti di accedere a opportunità immobiliari esclusive a prezzi vantaggiosi.' 
                  : 'Our team of experts guides you through the complex world of real estate auctions. We provide pre-auction consulting, assistance during the procedure, and post-award support, allowing you to access exclusive real estate opportunities at advantageous prices.'}
              </ServiceDescription>
              <Button 
                variant="primary" 
                to="/services/auctions"
                style={{ alignSelf: 'flex-start', marginTop: '1rem', backgroundColor: '#004744', borderColor: '#004744' }}
              >
                {language === 'it' ? 'Scopri' : 'Discover'}
              </Button>
            </TextContent>
          </Column>
          <Column $isRight>
            <ServiceImage $backgroundImage={auctionsImage} />
          </Column>
        </ServiceSection>
        
        {/* Servizio 4: Compravendite */}
        <ServiceSection id="buy-sell">
          <Column>
            <ServiceImage $backgroundImage={buyAndSellImage} />
          </Column>
          <Column $isRight>
            <TextContent>
              <ServiceTitle>{language === 'it' ? 'Compravendite' : 'Buy & Sell'}</ServiceTitle>
              <ServiceDescription>
                {language === 'it' 
                  ? 'Che tu stia cercando di acquistare o vendere una proprietà, il nostro team di professionisti immobiliari è qui per aiutarti. Offriamo una consulenza completa, dalla valutazione iniziale alla negoziazione e alla finalizzazione della transazione, garantendo sempre i migliori risultati.' 
                  : 'Whether you are looking to buy or sell a property, our team of real estate professionals is here to help. We offer comprehensive consulting, from initial valuation to negotiation and transaction finalization, always ensuring the best results.'}
              </ServiceDescription>
              <Button 
                variant="primary" 
                to="/services/buy-sell"
                style={{ alignSelf: 'flex-start', marginTop: '1rem', backgroundColor: '#004744', borderColor: '#004744' }}
              >
                {language === 'it' ? 'Scopri' : 'Discover'}
              </Button>
            </TextContent>
          </Column>
        </ServiceSection>
        
        {/* Servizio 5: Eventi */}
        <ServiceSection id="events">
          <Column>
            <TextContent>
              <ServiceTitle>{language === 'it' ? 'Eventi' : 'Events'}</ServiceTitle>
              <ServiceDescription>
                {language === 'it' 
                  ? 'Trasforma le nostre location esclusive nel palcoscenico perfetto per il tuo evento. Che si tratti di una celebrazione privata, un evento aziendale o una cerimonia speciale, il nostro team dedicato si occupa di ogni dettaglio, dalla pianificazione all\'esecuzione, per creare un\'esperienza memorabile.' 
                  : 'Transform our exclusive locations into the perfect stage for your event. Whether it\'s a private celebration, a corporate event, or a special ceremony, our dedicated team takes care of every detail, from planning to execution, to create a memorable experience.'}
              </ServiceDescription>
              <Button 
                variant="primary" 
                to="/services/events"
                style={{ alignSelf: 'flex-start', marginTop: '1rem', backgroundColor: '#004744', borderColor: '#004744' }}
              >
                {language === 'it' ? 'Scopri' : 'Discover'}
              </Button>
            </TextContent>
          </Column>
          <Column $isRight>
            <ServiceImage $backgroundImage={eventsImage} />
          </Column>
        </ServiceSection>
        
        {/* Servizio 6: Servizi/Experience */}
        <ServiceSection id="experiences">
          <Column>
            <ServiceImage $backgroundImage={experiencesImage} />
          </Column>
          <Column $isRight>
            <TextContent>
              <ServiceTitle>{language === 'it' ? 'Servizi/Experience' : 'Services/Experience'}</ServiceTitle>
              <ServiceDescription>
                {language === 'it' 
                  ? 'Arricchisci il tuo soggiorno con le nostre esperienze personalizzate. Dai tour enogastronomici ai corsi di cucina, dalle escursioni private alle lezioni di italiano, offriamo una vasta gamma di servizi esclusivi progettati per soddisfare i tuoi interessi e creare ricordi indimenticabili.' 
                  : 'Enrich your stay with our personalized experiences. From food and wine tours to cooking classes, from private excursions to Italian lessons, we offer a wide range of exclusive services designed to cater to your interests and create unforgettable memories.'}
              </ServiceDescription>
              <Button 
                variant="primary" 
                to="/services/experiences"
                style={{ alignSelf: 'flex-start', marginTop: '1rem', backgroundColor: '#004744', borderColor: '#004744' }}
              >
                {language === 'it' ? 'Scopri' : 'Discover'}
              </Button>
            </TextContent>
          </Column>
        </ServiceSection>
        
        {/* Modal for Short-term Rentals */}
        {activeModal === 'shortTerm' && (
          <ModalOverlay onClick={closeModal}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <CloseButton onClick={closeModal}>×</CloseButton>
              <ModalBody>
                <ModalTitle>{language === 'it' ? 'Affitti Brevi' : 'Short-term Rentals'}</ModalTitle>
                <ModalDescription>
                  {language === 'it' 
                    ? 'I nostri servizi di affitti brevi sono progettati per offrire soluzioni flessibili e di alta qualità sia per proprietari che per ospiti. Che tu stia cercando di affittare il tuo immobile o di trovare la sistemazione perfetta per il tuo soggiorno, possiamo aiutarti.'
                    : 'Our short-term rental services are designed to offer flexible, high-quality solutions for both property owners and guests. Whether you\'re looking to rent out your property or find the perfect accommodation for your stay, we can help.'}
                </ModalDescription>
                
                <ButtonsContainer>
                  <ActionButton 
                    variant="green" 
                    to="/services/property-owner"
                  >
                    {language === 'it' ? 'Vuoi dare in affitto?' : 'Want to rent out?'}
                    <br/>
                    <span style={{ fontSize: '0.9rem' }}>{language === 'it' ? 'Clicca Qui' : 'Click Here'}</span>
                  </ActionButton>
                  
                  <ActionButton 
                    variant="green" 
                    to="/services/property-tenant"
                  >
                    {language === 'it' ? 'Vuoi avere in affitto?' : 'Want to rent?'}
                    <br/>
                    <span style={{ fontSize: '0.9rem' }}>{language === 'it' ? 'Clicca Qui' : 'Click Here'}</span>
                  </ActionButton>
                </ButtonsContainer>
              </ModalBody>
            </ModalContent>
          </ModalOverlay>
        )}
        
        {/* Long-term Rentals now navigates directly; modal removed */}
      </ContentContainer>
    </PageContainer>
  );
};

export default Services; 